/// <amd-module name="Feed/Connect/Components/CopyableText" />
import * as React from "react";

import { ClipboardButton, IClipboardButtonProps } from "VSSUI/Clipboard";
import { css } from "VSSUI/Util";

export interface ICopyableTextProps {
    /**
     * Class name to apply to the outer element
     */
    className?: string;

    /**
     * Optional class name to apply to the inner 'content' element
     */
    contentClassName?: string;

    /**
     * Optional text to copy to the clipboard.
     * If no child components are supplied, this value will be used for the rendered content
     */
    text?: string;

    /**
     * Optional props to pass to the clipboard button.
     */
    buttonProps?: Omit<IClipboardButtonProps, "getContent">;

    /**
     * Optional text to scope copyable text for accessibility.
     * @default ""
     */
    ariaLabel?: string;
}

/**
 * Renders text (or child elements) with a 'copy' button. On click, the innerText
 * or supplied 'text' property value will be copied to the browser clipboard.
 *
 * If child components are supplied, they will be rendered, otherwise 'text' prop value will be used
 */
export const CopyableText: React.FunctionComponent<ICopyableTextProps> = ({
    className,
    contentClassName,
    text,
    buttonProps,
    children
}) => {
    const c = React.createRef<HTMLDivElement>();
    return (
        <div className={css(className, "copyable-text flex-row")}>
            <div className={css(contentClassName, "flex-grow copyable-content")} ref={c}>
                {children || text}
            </div>
            <ClipboardButton {...buttonProps} getContent={() => text || c.current!} />
        </div>
    );
};
